/**
 * ==============================================================================
 * AuthContext.jsx - Authentication State Management
 * ==============================================================================
 * 
 * This React Context manages all authentication-related state and operations:
 * - User login/logout
 * - JWT token storage and management
 * - Automatic axios header configuration
 * - User session persistence across page reloads
 * 
 * USAGE:
 * ------
 * 1. Wrap your app with <AuthProvider> in App.js
 * 2. Use the useAuth() hook in any component to access auth state
 * 
 * EXAMPLE:
 * --------
 * const { user, login, logout, isAdmin } = useAuth();
 * 
 * // Login
 * await login('email@example.com', 'password');
 * 
 * // Check if user is admin
 * if (isAdmin()) { ... }
 * 
 * // Logout
 * logout();
 * 
 * TOKEN STORAGE:
 * --------------
 * - Token is stored in localStorage with key 'synpop_token'
 * - IMPORTANT: Always use 'synpop_token', not 'token' when accessing localStorage
 * - Token is automatically added to axios default headers
 * 
 * ==============================================================================
 */

import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

// API base URL - use relative path so it works on any domain (preview, deployed, etc.)
const API = '/api';

// Create the context (null default, will be provided by AuthProvider)
const AuthContext = createContext(null);

/**
 * AuthProvider Component
 * ----------------------
 * Wraps the application and provides authentication state to all children.
 * Handles token persistence and automatic user fetching on app load.
 */
export const AuthProvider = ({ children }) => {
  // Current user object (null if not logged in)
  const [user, setUser] = useState(null);
  
  // JWT token (loaded from localStorage on mount)
  // IMPORTANT: Key is 'synpop_token' - do not change this!
  const [token, setToken] = useState(localStorage.getItem('synpop_token'));
  
  // Loading state for initial auth check
  const [loading, setLoading] = useState(true);

  /**
   * Effect: Initialize auth state on mount
   * - If token exists in localStorage, set axios header and fetch user
   * - If no token, mark loading as complete
   */
  useEffect(() => {
    if (token) {
      // Set default Authorization header for all axios requests
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  /**
   * Fetch current user from /auth/me endpoint
   * Called automatically when token is present
   */
  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (error) {
      // Token is invalid or expired - logout user
      console.error('Auth error:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  /**
   * Login Function
   * --------------
   * Authenticates user with email/password and stores JWT token
   * Handles MFA if enabled for the user
   * 
   * @param {string} email - User's email address
   * @param {string} password - User's password
   * @param {string} mfaCode - Optional MFA code if required
   * @returns {Object} User data on successful login
   * @throws {Error} On authentication failure or MFA_REQUIRED
   */
  const login = async (email, password, mfaCode = null) => {
    try {
      // Use the MFA-aware endpoint if MFA code is provided
      const endpoint = mfaCode ? `${API}/auth/login-mfa` : `${API}/auth/login`;
      const payload = mfaCode 
        ? { email, password, mfa_code: mfaCode }
        : { email, password };
      
      const response = await axios.post(endpoint, payload);
      const { token: newToken, user: userData } = response.data;
      
      // Store token in localStorage (key: 'synpop_token')
      localStorage.setItem('synpop_token', newToken);
      
      // Set axios default header for subsequent requests
      axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
      
      // Update state
      setToken(newToken);
      setUser(userData);
      
      return userData;
    } catch (error) {
      // Check if MFA is required
      if (error.response?.status === 403 && error.response?.data?.detail === 'MFA_REQUIRED') {
        // Throw a special error that the login form can handle
        const mfaError = new Error('MFA_REQUIRED');
        mfaError.isMfaRequired = true;
        throw mfaError;
      }
      throw error;
    }
  };

  /**
   * Register Function
   * -----------------
   * Creates new user account and logs them in automatically
   * 
   * @param {string} name - User's display name
   * @param {string} email - User's email address
   * @param {string} password - User's password
   * @returns {Object} User data on successful registration
   */
  const register = async (name, email, password) => {
    const response = await axios.post(`${API}/auth/register`, { name, email, password });
    const { token: newToken, user: userData } = response.data;
    
    localStorage.setItem('synpop_token', newToken);
    axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
    setToken(newToken);
    setUser(userData);
    
    return userData;
  };

  /**
   * Logout Function
   * ---------------
   * Clears authentication state and removes stored token
   */
  const logout = () => {
    // Remove token from localStorage
    localStorage.removeItem('synpop_token');
    
    // Remove Authorization header from axios defaults
    delete axios.defaults.headers.common['Authorization'];
    
    // Clear state
    setToken(null);
    setUser(null);
  };

  /**
   * Check if current user is admin
   * @returns {boolean} True if user has admin role
   */
  const isAdmin = () => {
    return user?.role === 'admin';
  };

  // Provide auth state and functions to children
  return (
    <AuthContext.Provider value={{ user, token, loading, login, register, logout, isAdmin }}>
      {children}
    </AuthContext.Provider>
  );
};

/**
 * useAuth Hook
 * ------------
 * Custom hook to access authentication context
 * Must be used within an AuthProvider
 * 
 * @returns {Object} Auth state and functions
 * @throws {Error} If used outside AuthProvider
 */
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
